// src/components/quizzes/LabelItGame.js
//
// LABEL IT! — drag name tiles onto the right spots on a picture.
// (Different from Drag and Drop: there the boxes are a row of cards; here the
// drop spots sit ON a picture — a world map, a plant, the parts of a cell.)
//
// Data (gameType "labelIt"; made in Layout Builder ▸ 🏷️ Label It! games):
//   questions[] = boards, played one after another ("levels"):
//     { question,            what to do, e.g. "Label the continents and oceans!"
//       image, imageAlt,     the picture (a URL) and what it shows
//       tiles: [{ id, text?, image?, x, y }] }
//   Each tile has its own drop spot at (x, y) — percent of the picture's
//   width / height — so adding a tile adds its spot.
//
// How it plays
//   * Tiles sit beside the picture on a wide screen (smart board, laptop) and
//     under it on a phone or a narrow window.
//   * DRAG a tile onto a spot with a finger, mouse or smart-board pen, or TAP a
//     tile, then TAP a spot. A tile dropped near a spot snaps to the nearest
//     one. Dropping on a taken spot sends that tile back to the tray.
//   * Tap a placed tile to pick it up again; tap the tray to put it back.
//   * "I'm finished" can be pressed any time. Each tile on its right spot is
//     worth 5 points; tiles left in the tray or on a wrong spot score nothing.
//     Right spots turn green, wrong or empty ones red, with the right name.
//   * 🔊 reads the instruction and the tiles aloud.
//
// onComplete({ score, details, answers }) — the same shapes Drag and Drop
// reports, so the Classroom and the teacher's answer list read it unchanged:
//   answers[] = { question: "Label the map — the spot for “Africa”",
//                 selectedText (what was put there, or "(empty)"),
//                 correctText: "Africa", correct }

import React, { useEffect, useMemo, useRef, useState } from "react";
import { playGameSound } from "../../services/gameSounds";
import { resolveImageSrc } from "../../utils/resolveImageSrc";
import { launchConfetti } from "../../utils/confetti";
import { sfx } from "./sfx";
import { QuizHud, useGameMusic, useReadAloud } from "./kit/QuizKit";
import "./labelIt.css";

const MUSIC =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/gamemusic%2Fdraganddrop.mp3?alt=media&token=fbfd52d1-f315-4948-91e2-f2202766d62f";
const DROP_SFX =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Fbongsound1.mp3?alt=media&token=f47df164-06b3-494e-ba16-5ab54f707306";
export const LABEL_IT_POINTS = 5;
const SNAP_PX = 80; // a drop this close to a spot's centre lands on it

const shuffle = (a) => {
  const x = [...a];
  for (let i = x.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [x[i], x[j]] = [x[j], x[i]];
  }
  return x;
};
const play = (url) => {
  try {
    const a = sfx(url);
    a.currentTime = 0;
    a.play().catch(() => {});
  } catch {
    /* ignore */
  }
};
const clamp = (n) => Math.max(0, Math.min(100, Number(n) || 0));
export const tileLabel = (t) => (t?.text && String(t.text).trim()) || (t?.image ? "[picture]" : "(blank)");

const Tile = ({ t, className = "", tone = 0, ...rest }) => (
  <button type="button" className={`li-tile is-c${tone % 6} ${t.image && !t.text ? "is-pic" : ""} ${className}`} data-tile={t.id} aria-label={tileLabel(t)} {...rest}>
    {t.image && <img src={resolveImageSrc(t.image)} alt="" draggable="false" />}
    {t.text && <span>{t.text}</span>}
  </button>
);

const LabelItGame = ({ questions = [], onComplete, topic }) => {
  const [bi, setBi] = useState(0); // which board
  const [score, setScore] = useState(0);
  const [rawPlace, setPlace] = useState({}); // spotId -> tileId
  const [picked, setPicked] = useState(null); // tileId (tap mode)
  const [drag, setDrag] = useState(null); // { id, x, y }
  const [over, setOver] = useState(null); // spotId under the finger
  const [checked, setChecked] = useState(false);
  const [aspect, setAspect] = useState(4 / 3);
  const details = useRef([]);
  const press = useRef(null);
  const boardRef = useRef(null);
  const [muted, toggleMute, duck] = useGameMusic(MUSIC);
  const voice = useReadAloud(duck);

  const board = questions[bi] || null;
  const tiles = useMemo(
    () => (board?.tiles || []).filter(Boolean).map((t, n) => ({ ...t, id: String(t.id ?? `t${n}`), x: clamp(t.x), y: clamp(t.y) })),
    [board]
  );
  const byId = useMemo(() => Object.fromEntries(tiles.map((t) => [t.id, t])), [tiles]);
  const order = useMemo(() => shuffle(tiles.map((t) => t.id)), [tiles]);

  useEffect(() => {
    setPlace({});
    setPicked(null);
    setChecked(false);
    voice.stop();
    voice.preload([{ key: "q", text: board?.question || "Drag each name to its place on the picture." }, ...tiles.map((t) => ({ key: `t:${t.id}`, text: tileLabel(t) }))]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bi]);

  if (!board) return null;

  // Only placements that belong to THIS picture. Right after "Next picture"
  // the old picture's placements are still in state for one render (the
  // reset runs in an effect); without this filter a tile from the last
  // picture is looked up on the new one and isn't there.
  const place = Object.fromEntries(Object.entries(rawPlace).filter(([spot, tile]) => byId[spot] && byId[tile]));

  const placedIds = new Set(Object.values(place));
  const tray = order.filter((id) => !placedIds.has(id));
  const placedCount = placedIds.size;

  const putOn = (tileId, spotId) => {
    if (checked || !tileId || !spotId) return;
    setPlace((p) => {
      const next = {};
      Object.entries(p).forEach(([s, t]) => {
        if (t !== tileId && s !== spotId) next[s] = t;
      });
      next[spotId] = tileId; // a tile already on that spot goes back to the tray
      return next;
    });
    setPicked(null);
    play(DROP_SFX);
  };
  const backToTray = (tileId) => {
    if (checked) return;
    setPlace((p) => Object.fromEntries(Object.entries(p).filter(([, t]) => t !== tileId)));
    setPicked(null);
  };

  // nearest spot to a screen point (within SNAP_PX), so a drop doesn't have
  // to land exactly on a small dot
  const spotAt = (x, y) => {
    const el = boardRef.current;
    if (!el) return null;
    let best = null;
    let bestD = Infinity;
    el.querySelectorAll("[data-spot]").forEach((s) => {
      const r = s.getBoundingClientRect();
      const d = Math.hypot(x - (r.left + r.width / 2), y - (r.top + r.height / 2));
      if (d < bestD) {
        bestD = d;
        best = s.getAttribute("data-spot");
      }
    });
    if (bestD <= SNAP_PX) return best;
    // inside the picture but far from every spot: no drop
    return null;
  };
  const trayAt = (x, y) => Boolean(document.elementFromPoint(x, y)?.closest?.("[data-tray]"));

  const onDown = (e, id) => {
    if (checked) return;
    press.current = { id, x: e.clientX, y: e.clientY, moved: false };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };
  const onMove = (e) => {
    const p = press.current;
    if (!p) return;
    if (!p.moved && Math.hypot(e.clientX - p.x, e.clientY - p.y) < 8) return;
    p.moved = true;
    setDrag({ id: p.id, x: e.clientX, y: e.clientY });
    setOver(spotAt(e.clientX, e.clientY));
  };
  const onUp = (e) => {
    const p = press.current;
    press.current = null;
    setDrag(null);
    setOver(null);
    if (!p) return;
    if (p.moved) {
      const s = spotAt(e.clientX, e.clientY);
      if (s) putOn(p.id, s);
      else if (trayAt(e.clientX, e.clientY)) backToTray(p.id);
      return;
    }
    setPicked((cur) => (cur === p.id ? null : p.id));
  };
  const tileHandlers = (id) => ({
    onPointerDown: (e) => onDown(e, id),
    onPointerMove: onMove,
    onPointerUp: onUp,
    onPointerCancel: () => {
      press.current = null;
      setDrag(null);
      setOver(null);
    },
  });

  const finish = () => {
    voice.stop();
    const pairs = tiles.map((t, idx) => {
      const got = place[t.id] || null; // spots share their tile's id
      return {
        pairIndex: idx,
        targetId: t.id,
        targetLabel: `the spot for “${tileLabel(t)}”`,
        studentId: got,
        studentLabel: got ? tileLabel(byId[got]) : "(empty)",
        correctId: t.id,
        correctLabel: tileLabel(t),
        isCorrect: got === t.id,
      };
    });
    const right = pairs.filter((x) => x.isCorrect).length;
    details.current[bi] = { qIndex: bi, question: board.question || `Board ${bi + 1}`, pairs };
    setScore((s) => s + right * LABEL_IT_POINTS);
    setChecked(true);
    playGameSound(right === pairs.length ? "correct" : "wrong", "labelIt", () => play(DROP_SFX));
    if (right === pairs.length && pairs.length) launchConfetti();
  };

  const next = () => {
    if (bi + 1 < questions.length) {
      setBi(bi + 1);
      return;
    }
    const list = details.current.filter(Boolean);
    const final = list.reduce((n, r) => n + r.pairs.filter((x) => x.isCorrect).length * LABEL_IT_POINTS, 0);
    const answers = list.flatMap((r) =>
      r.pairs.map((x) => ({
        question: `${r.question} — ${x.targetLabel}`,
        selectedText: x.studentLabel,
        correctText: x.correctLabel,
        correct: Boolean(x.isCorrect),
      }))
    );
    onComplete?.({ score: final, details: list, answers });
  };

  const rightNow = checked ? tiles.filter((t) => place[t.id] === t.id).length : 0;
  const parts = [{ key: "q", text: board.question || "Drag each name to its place on the picture." }, ...tray.map((id) => ({ key: `t:${id}`, text: tileLabel(byId[id]) }))];
  const reading = voice.partIndex === null ? null : parts[voice.partIndex]?.key;
  const dragged = drag ? byId[drag.id] : null;

  return (
    <div className="qk-scene li-scene" onPointerMove={drag ? onMove : undefined}>
      <div className="qk-wrap li-wrap">
        <QuizHud title={topic?.title || ""} index={bi} total={questions.length} score={score} muted={muted} onMute={toggleMute} />

        <section className={`qk-question li-question ${reading === "q" ? "is-reading" : ""}`} key={`q${bi}`}>
          <h2 className="qk-question-text">{board.question || "Drag each name to its place on the picture!"}</h2>
          <span className="qk-read-hint">Drag each tile onto its spot — or tap a tile, then tap the spot.</span>
          {voice.can && (
            <button type="button" className={`qk-read ${voice.reading ? "is-on" : ""}`} onClick={() => (voice.reading ? voice.stop() : voice.read(parts))} aria-label={voice.reading ? "Stop reading" : "Read it to me"}>
              {voice.reading ? "■" : "🔊"}
            </button>
          )}
        </section>

        <div className="li-play">
          <div className="li-board-wrap">
            <div className="li-board" ref={boardRef} style={{ width: `min(100%, calc(${(aspect * 58).toFixed(2)}vh))` }}>
              {board.image ? (
                <img
                  className="li-board-img"
                  src={resolveImageSrc(board.image)}
                  alt={board.imageAlt || ""}
                  draggable="false"
                  onLoad={(e) => {
                    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                    if (w && h) setAspect(w / h);
                  }}
                />
              ) : (
                <div className="li-board-img is-empty" />
              )}
              {tiles.map((t) => {
                const onIt = place[t.id];
                const state = checked ? (onIt === t.id ? "is-right" : "is-wrong") : over === t.id ? "is-over" : picked && !onIt ? "is-ready" : "";
                return (
                  <div
                    key={t.id}
                    className={`li-spot ${onIt ? "is-full" : ""} ${state}`}
                    data-spot={t.id}
                    style={{ left: `${t.x}%`, top: `${t.y}%` }}
                    onClick={() => {
                      if (picked && picked !== onIt) putOn(picked, t.id);
                    }}
                  >
                    {onIt ? (
                      <Tile
                        t={byId[onIt]}
                        tone={order.indexOf(onIt)}
                        className={`is-placed ${picked === onIt ? "is-picked" : ""} ${drag?.id === onIt ? "is-ghost" : ""}`}
                        {...tileHandlers(onIt)}
                      />
                    ) : (
                      <span className="li-dot" aria-label="empty spot" />
                    )}
                    {checked && onIt !== t.id && <span className="li-was">✔ {tileLabel(t)}</span>}
                  </div>
                );
              })}
            </div>
          </div>

          <aside className="li-side">
            <p className="li-tray-label">{checked ? "Results" : `Tiles · ${placedCount} of ${tiles.length} placed`}</p>
            <div
              className="li-tray"
              data-tray
              onClick={(e) => {
                if (picked && placedIds.has(picked) && e.target === e.currentTarget) backToTray(picked);
              }}
            >
              {checked ? (
                <div className="li-result">
                  <b>
                    {rightNow} of {tiles.length}
                  </b>
                  <span>right · +{rightNow * LABEL_IT_POINTS} points</span>
                  {rightNow < tiles.length && <small>The red spots show the right names.</small>}
                </div>
              ) : tray.length === 0 ? (
                <span className="li-tray-empty">All placed — press “I’m finished!”</span>
              ) : (
                tray.map((id) => (
                  <Tile
                    key={id}
                    t={byId[id]}
                    tone={order.indexOf(id)}
                    className={`${picked === id ? "is-picked" : ""} ${drag?.id === id ? "is-ghost" : ""} ${reading === `t:${id}` ? "is-reading" : ""}`}
                    {...tileHandlers(id)}
                  />
                ))
              )}
            </div>

            <div className="qk-actions li-actions">
              {!checked ? (
                <>
                  <button type="button" className="qk-btn" onClick={finish}>
                    ✔ I’m finished!
                  </button>
                  {placedCount > 0 && (
                    <button type="button" className="qk-btn is-soft" onClick={() => setPlace({})}>
                      ↺ Start over
                    </button>
                  )}
                </>
              ) : (
                <button type="button" className="qk-btn" onClick={next}>
                  {bi + 1 < questions.length ? "Next picture ➜" : "Finish 🎉"}
                </button>
              )}
            </div>
            {!checked && tray.length > 0 && placedCount > 0 && <p className="qk-hint li-hint">Tiles left in the tray don’t score.</p>}
          </aside>
        </div>
      </div>

      {dragged && (
        <div className="qk-drag-ghost li-ghost" style={{ left: drag.x, top: drag.y }}>
          <Tile t={dragged} tone={order.indexOf(dragged.id)} tabIndex={-1} />
        </div>
      )}
    </div>
  );
};

export default LabelItGame;
